// 작품·회차 도메인 — 서버(api/internal/serial)와 같은 규칙을 화면에서 쓴다. 외부 라이브러리 없음 (DOMAIN_PURITY)

/** AI 사용 정도 — 작품 단위로 작가가 밝힌다. 서버 works.ai_usage 와 같은 세 값 */
export const AI_USAGES = ['none', 'assisted', 'generated'] as const;
export type AiUsage = (typeof AI_USAGES)[number];

export function isAiUsage(value: string): value is AiUsage {
  return (AI_USAGES as readonly string[]).includes(value);
}

/** 서버와 같은 상한 (api/internal/serial/serial.go MaxTitleChars·MaxSynopsisChars). 글자 수는 코드 포인트 수 */
export const MAX_TITLE_CHARS = 60;
export const MAX_SYNOPSIS_CHARS = 1000;

export interface Work {
  readonly id: string;
  readonly title: string;
  readonly synopsis: string;
  readonly aiUsage: AiUsage;
  readonly episodeCount: number;
  readonly createdAt: string;
  readonly updatedAt: string;
  /** 로그인한 내가 쓴 작품인지 — 서버는 작가 식별자를 내려주지 않는다 */
  readonly mine: boolean;
}

export interface EpisodeSummary {
  readonly id: string;
  readonly no: number;
  readonly title: string;
  readonly charCount: number;
  readonly publishedAt: string;
}

export interface Episode extends EpisodeSummary {
  readonly workId: string;
  readonly body: string;
  /** 앞뒤 공개 회차, 없으면 빈 문자열 */
  readonly prevId: string;
  readonly nextId: string;
  /** 로그인한 내가 쓴 회차인지 — 내리기 버튼을 보일지 */
  readonly mine: boolean;
}

export interface WorksPage {
  readonly works: readonly Work[];
  /** 다음 장이 없으면 빈 문자열 */
  readonly nextCursor: string;
}

export interface WorkDetail {
  readonly work: Work;
  readonly episodes: readonly EpisodeSummary[];
}

/** 공백을 뺀 글자 수 — 서버 serial.CharCount 와 같은 값. 화면 글자 수 표시와 공개 버튼 판단에 쓴다 */
export function charCount(text: string): number {
  let n = 0;
  for (const ch of text) if (!/\s/u.test(ch)) n++;
  return n;
}

export function codePoints(text: string): number {
  return [...text].length;
}

/** 회차 표시 이름 — 제목을 비워 서버가 「N화」로 채웠으면 그대로, 직접 쓴 제목이면 「N화 · 제목」 */
export function episodeLabel(no: number, title: string): string {
  const auto = `${no}화`;
  return title === auto ? auto : `${auto} · ${title}`;
}

/** 서버 오류 코드 → 문구 키. 모르는 코드는 failed 로 모은다 (서버 api.go fail 과 짝) */
const ERROR_KEYS: Readonly<Record<string, string>> = {
  login_required: 'error.login_required',
  token_required: 'error.login_required',
  token_invalid: 'error.login_required',
  not_author: 'error.not_author',
  not_found: 'error.not_found',
  empty_body: 'error.empty_body',
  too_short: 'error.too_short',
  daily_limit_reached: 'error.daily_limit',
  invalid_field: 'error.invalid_field',
  too_large: 'error.too_large',
  network: 'error.network',
};

export function errorMessageKey(code: string): string {
  return ERROR_KEYS[code] ?? 'error.failed';
}
